import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdViewer from '../components/ads/AdViewer';
import {
  CampaignCreativeType,
  CampaignPackage,
  getPackagePriceForCreative,
  getPackageStartingPrice,
  listPackages,
} from '../api/packages';
import { listVideos } from '../api/videos';
import useAuth from '../hooks/useAuth';

const DEFAULT_THUMBNAIL_URL =
  'https://via.placeholder.com/1200x600/1f2937/ffffff.png?text=TELLURVISION+Ad';

const isYouTubeUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return host.includes('youtube.com') || host.includes('youtu.be');
  } catch {
    return false;
  }
};

const toYouTubeThumbnailUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    let videoId: string | null = null;

    if (host.includes('youtube.com')) {
      if (parsed.searchParams.has('v')) {
        videoId = parsed.searchParams.get('v');
      }
    } else if (host.includes('youtu.be')) {
      videoId = parsed.pathname.replace(/^\//, '') || null;
    }

    if (!videoId) return null;

    return `https://img.youtube.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
  } catch {
    return null;
  }
};

interface AdFromApi {
  _id: string;
  title: string;
  description: string;
  creativeType?: CampaignCreativeType;
  fundingStatus?: 'draft' | 'unfunded' | 'funded';
  videoUrl?: string;
  imageUrl?: string;
  categories?: string[];
  tags?: string[];
  views?: number;
  clicks?: number;
  createdAt?: string;
}

type AuthUserWithId = {
  _id?: string;
  user?: {
    _id?: string;
  };
};

type WizardStep = 1 | 2 | 3;
type StaticCreativePricingType = 'text' | 'image';

const getCreativePricingType = (
  ad: AdFromApi | null,
  staticCreativePricingType: StaticCreativePricingType,
): CampaignCreativeType => {
  if (ad?.creativeType) {
    return ad.creativeType;
  }

  if (ad?.videoUrl && ad.videoUrl.trim()) {
    return 'video';
  }

  return staticCreativePricingType;
};

const getSuggestedBudgetForPackage = (
  pkg: CampaignPackage,
  creativeType: CampaignCreativeType,
): number => {
  const directPrice = getPackagePriceForCreative(pkg, creativeType);
  if (directPrice > 0) {
    return directPrice;
  }

  if (typeof pkg.minAmount === 'number' && pkg.minAmount > 0) {
    return pkg.minAmount;
  }

  return 0;
};

const formatCreativePricingType = (creativeType: CampaignCreativeType): string =>
  creativeType === 'text' ? 'Text ad' : creativeType === 'image' ? 'Image ad' : 'Video ad';

const formatDisplayDuration = (seconds?: number): string =>
  typeof seconds === 'number' && seconds > 0 ? `${seconds}-second display` : 'default rotation length';

const StartCampaign: React.FC = () => {
  // Example pricing for each channel (replace with your real pricing logic)
  const BILLBOARD_PRICE = 100;
  const SOCIAL_PRICE = 50;
  const TEXT_PRICE = 25;
    // ...existing state declarations...
    const [aiGoal, setAiGoal] = useState<string>('awareness');
    const [aiBillboard, setAiBillboard] = useState<boolean>(true);
    const [aiSocial, setAiSocial] = useState<boolean>(true);
    const [aiText, setAiText] = useState<boolean>(false);

    // Calculate real-time price based on selected channels
    const channelPrice = (aiBillboard ? BILLBOARD_PRICE : 0) + (aiSocial ? SOCIAL_PRICE : 0) + (aiText ? TEXT_PRICE : 0);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<WizardStep>(1);
  const [packages, setPackages] = useState<CampaignPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<CampaignPackage | null>(null);
  const [budget, setBudget] = useState<string>('');

  const [ads, setAds] = useState<AdFromApi[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState<string | null>(null);
  const [selectedAd, setSelectedAd] = useState<AdFromApi | null>(null);
  const [staticCreativePricingType, setStaticCreativePricingType] =
    useState<StaticCreativePricingType>('image');


  const resolvedUserId: string | undefined = (() => {
    if (!user) return undefined;
    const authUser = user as AuthUserWithId;
    if (authUser.user && typeof authUser.user._id === 'string') return authUser.user._id;
    if (typeof authUser._id === 'string') return authUser._id;
    return undefined;
  })();

  useEffect(() => {
    let cancelled = false;
    const fetchPackages = async () => {
      setPackagesLoading(true);
      setPackagesError(null);
      try {
        const data = await listPackages();
        if (!cancelled) {
          setPackages(data);
          if (!selectedPackage && data.length > 0) {
            const first = data.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0];
            setSelectedPackage(first);
            const initialBudget = getSuggestedBudgetForPackage(first, 'image');
            if (initialBudget > 0) {
              setBudget(String(initialBudget));
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load campaign packages';
          setPackagesError(message);
        }
      } finally {
        if (!cancelled) {
          setPackagesLoading(false);
        }
      }
    };

    void fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [selectedPackage]);

  useEffect(() => {
    if (step !== 2) return;
    let cancelled = false;

    const fetchAds = async () => {
      setAdsLoading(true);
      setAdsError(null);
      try {
        const params: Record<string, unknown> = {
          includeMeta: true,
          sortBy: 'createdAt',
          sortDir: 'desc',
          page: 1,
          pageSize: 6,
        };
        if (resolvedUserId) {
          params.userId = resolvedUserId;
        }

        const data = (await listVideos(params)) as
          | AdFromApi[]
          | { items?: AdFromApi[]; total?: number }
          | null
          | undefined;

        if (cancelled) return;

        if (data && !Array.isArray(data) && Array.isArray(data.items)) {
          setAds(data.items);
        } else if (Array.isArray(data)) {
          setAds(data);
        } else {
          setAds([]);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load your ads';
          setAdsError(message);
          setAds([]);
        }
      } finally {
        if (!cancelled) {
          setAdsLoading(false);
        }
      }
    };

    void fetchAds();
    return () => {
      cancelled = true;
    };
  }, [step, resolvedUserId]);

  useEffect(() => {
    if (selectedAd?.videoUrl && selectedAd.videoUrl.trim()) {
      return;
    }

    setStaticCreativePricingType('image');
  }, [selectedAd?._id, selectedAd?.videoUrl]);

  const parsedBudget = (() => {
    const value = Number(budget);
    return Number.isFinite(value) && value > 0 ? value : null;
  })();

  const selectedCreativePricingType = getCreativePricingType(
    selectedAd,
    staticCreativePricingType,
  );
  const suggestedBudgetForSelection = selectedPackage
    ? getSuggestedBudgetForPackage(selectedPackage, selectedCreativePricingType)
    : null;

  // Removed unused handleAiRecommend and AI error/date state

  const handleNextFromStep1 = () => {
    if (!selectedPackage) return;
    if (!parsedBudget) return;
    setStep(2);
  };

  const handleNextFromStep2 = () => {
    if (!selectedAd) return;
    setStep(3);
  };

  const handleConfirm = () => {
    if (!selectedPackage || !parsedBudget) return;
    navigate('/payment', {
      state: {
        fromCampaignWizard: true,
        budget: parsedBudget,
        packageId: selectedPackage._id,
        packageName: selectedPackage.name,
        adId: selectedAd ? selectedAd._id : null,
        creativeType: selectedCreativePricingType,
        displayDurationSeconds: selectedPackage.displayDurationSeconds,
      },
    });
  };

  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-5xl px-4 py-6 space-y-6 sm:py-8">
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">
              Start a campaign
            </h1>
            <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-400">
              Choose a TELLURVISION package, pair it with one of your creatives, and then
              continue to payments to fund your campaign.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go home
          </button>
        </header>

        <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: step === 1 ? '#1d4ed8' : '#9ca3af' }}
            />
            Package
          </span>
          <span className="text-slate-400">→</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: step >= 2 ? '#1d4ed8' : '#9ca3af' }}
            />
            Creative
          </span>
          <span className="text-slate-400">→</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: step === 3 ? '#1d4ed8' : '#9ca3af' }}
            />
            Review
          </span>
        </div>

        {step === 1 && (
          <section className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Choose a package</h2>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  Pick the campaign mix that best matches your goals. You can fine-tune the budget before continuing.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Campaign goal</label>
                <select
                  value={aiGoal}
                  onChange={(e) => setAiGoal(e.target.value)}
                  className="min-w-[170px] w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="awareness">Brand awareness</option>
                  <option value="visits">Store / site visits</option>
                  <option value="leads">Leads or signups</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Channels</label>
                <div className="flex flex-wrap gap-2 text-xs">
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={aiBillboard}
                      onChange={(e) => setAiBillboard(e.target.checked)}
                      className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Billboard</span>
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={aiSocial}
                      onChange={(e) => setAiSocial(e.target.checked)}
                      className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Social / share links</span>
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={aiText}
                      onChange={(e) => setAiText(e.target.checked)}
                      className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Text</span>
                  </label>
                </div>
                <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">Select one or more channels. Billboard displays your ad on physical screens; Social/share links promote your ad online; Text is for SMS or text-based delivery.</p>
              </div>
              <div className="rounded-md bg-blue-50 p-3 text-xs text-blue-900 border border-blue-200 min-w-[180px]">
                <div className="font-semibold mb-1">Estimated Price</div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span>Billboard</span>
                    <span>${BILLBOARD_PRICE.toFixed(2)} {aiBillboard ? '' : '(not selected)'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Social / Share Links</span>
                    <span>${SOCIAL_PRICE.toFixed(2)} {aiSocial ? '' : '(not selected)'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Text</span>
                    <span>${TEXT_PRICE.toFixed(2)} {aiText ? '' : '(not selected)'}</span>
                  </div>
                  <div className="flex items-center justify-between font-bold border-t border-blue-200 pt-1 mt-1">
                    <span>Total</span>
                    <span>${channelPrice.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-1 text-[10px] text-blue-700">This total updates as you select channels. Actual payment may vary if you adjust your budget later.</div>
              </div>
            </div>

            {packagesLoading && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Loading campaign packages…</p>
            )}
            {packagesError && <p className="text-xs text-red-600">{packagesError}</p>}

            {!packagesLoading && !packagesError && packages.length > 0 && (
              <div className="grid gap-3 md:grid-cols-3">
                {packages
                  .slice()
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((pkg) => {
                    const isSelected = selectedPackage && selectedPackage._id === pkg._id;
                    return (
                      <button
                        key={pkg._id}
                        type="button"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          const initial = getSuggestedBudgetForPackage(pkg, 'image');
                          if (initial > 0) {
                            setBudget(String(initial));
                          }
                        }}
                        className={`flex flex-col rounded-lg border px-3 py-2 text-left text-xs shadow-sm transition hover:border-blue-500 hover:bg-blue-50/40 ${isSelected ? 'border-blue-600 bg-blue-50/70' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900'}`}
                      >
                        <span className="text-[11px] font-semibold text-slate-900 dark:text-slate-100">
                          {pkg.name}
                        </span>
                        {pkg.description && (
                          <span className="mt-1 line-clamp-3 text-[10px] text-slate-600 dark:text-slate-400">
                            {pkg.description}
                          </span>
                        )}
                        <span className="mt-2 text-[11px] font-medium text-slate-900 dark:text-slate-100">
                          Starting at ${getPackageStartingPrice(pkg).toFixed(2)}
                        </span>
                        <span className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                          {formatDisplayDuration(pkg.displayDurationSeconds)}
                        </span>
                        <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                          <span>Text ${getPackagePriceForCreative(pkg, 'text').toFixed(2)}</span>
                          <span>Image ${getPackagePriceForCreative(pkg, 'image').toFixed(2)}</span>
                          <span>Video ${getPackagePriceForCreative(pkg, 'video').toFixed(2)}</span>
                        </div>
                        {(pkg.minAmount || pkg.maxAmount) && (
                          <span className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                            {pkg.minAmount && pkg.maxAmount
                              ? `Suggested range $${pkg.minAmount.toFixed(0)}–$${pkg.maxAmount.toFixed(0)}`
                              : pkg.minAmount
                                ? `Minimum $${pkg.minAmount.toFixed(0)}`
                                : pkg.maxAmount
                                  ? `Up to $${pkg.maxAmount.toFixed(0)}`
                                  : null}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}

            <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 dark:border-slate-700 pt-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                  Campaign budget (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="mt-1 w-40 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                  You can adjust this later on the Payments page as well.
                </p>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={handleNextFromStep1}
                  disabled={!selectedPackage || !parsedBudget}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  Next: Choose creative
                </button>
              </div>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Choose a creative</h2>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  Pick which ad creative should run with this campaign. You can always come back
                  and change this later.
                </p>
              </div>
              <div className="flex gap-2">
                <Link
                  to="/share"
                  className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
                >
                  Create / share ad
                </Link>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
                >
                  Back
                </button>
              </div>
            </div>

            {adsLoading && <p className="text-xs text-slate-500 dark:text-slate-400">Loading your ads…</p>}
            {adsError && <p className="text-xs text-red-600">{adsError}</p>}

            {!adsLoading && !adsError && ads.length === 0 && (
              <div className="space-y-3 text-xs text-slate-600 dark:text-slate-400">
                <p>
                  We could not find any creatives associated with your account yet. You can create
                  a new ad from the Create / share ad screen and come back here once it is saved.
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  You can still continue and fund this campaign now. Your payment will be linked
                  to the selected package, and you can attach a creative later.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/share"
                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700"
                  >
                    Go create an ad
                  </Link>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
                  >
                    Continue without creative
                  </button>
                </div>
              </div>
            )}

            {ads.length > 0 && (
              <div className="grid gap-3 md:grid-cols-3">
                {ads.map((ad) => {
                  const isSelected = selectedAd && selectedAd._id === ad._id;
                  return (
                    <button
                      key={ad._id}
                      type="button"
                      onClick={() => {
                        setSelectedAd(ad);
                        if (!ad.videoUrl?.trim()) {
                          setStaticCreativePricingType('image');
                        }
                      }}
                      className={`flex flex-col overflow-hidden rounded-lg border text-left text-xs shadow-sm transition hover:border-blue-500 hover:bg-blue-50/40 ${isSelected ? 'border-blue-600 bg-blue-50/70' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900'}`}
                    >
                      <div className="aspect-video w-full overflow-hidden bg-black">
                        <img
                          src={
                            ad.imageUrl?.trim()
                              ? ad.imageUrl
                              : isYouTubeUrl(ad.videoUrl ?? undefined)
                                ? toYouTubeThumbnailUrl(ad.videoUrl as string) ||
                                  DEFAULT_THUMBNAIL_URL
                                : DEFAULT_THUMBNAIL_URL
                          }
                          alt={ad.title}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="flex flex-1 flex-col px-2 py-2">
                        <p className="line-clamp-2 text-[11px] font-semibold text-slate-900 dark:text-slate-100">
                          {ad.title}
                        </p>
                        <p className="mt-1 line-clamp-3 text-[10px] text-slate-600 dark:text-slate-400">
                          {ad.description}
                        </p>
                        <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                          <span>
                            {typeof ad.views === 'number' &&
                              `${ad.views} view${ad.views === 1 ? '' : 's'}`}
                          </span>
                          <span>{ad.videoUrl?.trim() ? 'Video ad' : 'Static ad'}</span>
                          {ad.createdAt && (
                            <span>{new Date(ad.createdAt).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedAd && (
              <div className="mt-3 grid gap-3 border-t border-slate-200 dark:border-slate-700 pt-3 text-sm md:grid-cols-2">
                <div>
                  <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300">Preview</h3>
                  <div className="mt-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2">
                    <AdViewer
                      ad={{
                        title: selectedAd.title,
                        description: selectedAd.description,
                        creativeType: selectedAd.creativeType,
                        fundingStatus: selectedAd.fundingStatus,
                        videoUrl: selectedAd.videoUrl,
                        imageUrl: selectedAd.imageUrl,
                        categories: selectedAd.categories,
                        tags: selectedAd.tags,
                        views: selectedAd.views,
                        clicks: selectedAd.clicks,
                        motionData: {},
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-between text-xs text-slate-600 dark:text-slate-400">
                  <div>
                    <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300">Details</h3>
                    <p className="mt-1">
                      <span className="font-medium">Package:</span> {selectedPackage?.name || '—'}
                    </p>
                    <p className="mt-1">
                      <span className="font-medium">Display:</span>{' '}
                      {formatDisplayDuration(selectedPackage?.displayDurationSeconds)}
                    </p>
                    {parsedBudget && (
                      <p className="mt-1">
                        <span className="font-medium">Planned budget:</span> ${
                          parsedBudget.toFixed(2)
                        }
                      </p>
                    )}
                    <p className="mt-1">
                      <span className="font-medium">Creative:</span> {selectedAd.title}
                    </p>
                    <p className="mt-1">
                      <span className="font-medium">Pricing type:</span>{' '}
                      {formatCreativePricingType(selectedCreativePricingType)}
                    </p>
                    {!selectedAd.videoUrl?.trim() && (
                      <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
                        <p className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                          How should this static creative be priced?
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(['text', 'image'] as const).map((creativeType) => {
                            const isActive = staticCreativePricingType === creativeType;
                            return (
                              <button
                                key={creativeType}
                                type="button"
                                onClick={() => setStaticCreativePricingType(creativeType)}
                                className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium ${isActive ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-100'}`}
                              >
                                {formatCreativePricingType(creativeType)}
                              </button>
                            );
                          })}
                        </div>
                        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                          Use text pricing for copy-first slides. Use image pricing for posters,
                          photos, or graphic-heavy layouts.
                        </p>
                      </div>
                    )}
                    {typeof suggestedBudgetForSelection === 'number' && suggestedBudgetForSelection > 0 && (
                      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700">
                          Recommended budget
                        </p>
                        <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                          ${suggestedBudgetForSelection.toFixed(2)}
                        </p>
                        <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">
                          Based on a {formatDisplayDuration(selectedPackage?.displayDurationSeconds)} and
                          the {formatCreativePricingType(selectedCreativePricingType).toLowerCase()} rate.
                        </p>
                        <button
                          type="button"
                          onClick={() => setBudget(suggestedBudgetForSelection.toFixed(2))}
                          className="mt-2 inline-flex items-center rounded-md border border-emerald-300 bg-white dark:bg-slate-900 px-3 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          Use suggested price
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleNextFromStep2}
                      className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700"
                    >
                      Next: Review
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {step === 3 && (
          <section className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Review & continue</h2>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  Confirm your package, budget, and creative. When you continue, you&apos;ll be
                  taken to the Payments page with this budget pre-filled.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
                >
                  Back
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Campaign summary
                </h3>
                <p>
                  <span className="font-medium">Package:</span>{' '}
                  {selectedPackage ? selectedPackage.name : 'Not selected'}
                </p>
                {selectedPackage?.description && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">{selectedPackage.description}</p>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Display:</span>{' '}
                  {formatDisplayDuration(selectedPackage?.displayDurationSeconds)}
                </p>
                {parsedBudget && (
                  <p className="mt-1">
                    <span className="font-medium">Planned budget:</span> ${
                      parsedBudget.toFixed(2)
                    }
                  </p>
                )}
                {typeof suggestedBudgetForSelection === 'number' && suggestedBudgetForSelection > 0 && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Recommended for this {formatCreativePricingType(selectedCreativePricingType).toLowerCase()}:
                    {' '}${suggestedBudgetForSelection.toFixed(2)}
                  </p>
                )}
                {selectedAd && (
                  <p className="mt-1 text-sm">
                    <span className="font-medium">Creative:</span> {selectedAd.title}
                  </p>
                )}
              </div>
              <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  What happens next
                </h3>
                <ol className="list-decimal pl-4 space-y-1">
                  <li>We&apos;ll send you to the Payments page.</li>
                  <li>Your budget will be pre-filled, but you can still adjust it.</li>
                  <li>
                    Once payment is processed, your campaign will appear in analytics and the ad
                    feed like other creatives.
                  </li>
                </ol>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!selectedPackage || !parsedBudget}
                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    Continue to payments
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default StartCampaign;
